import React, { useCallback, useMemo, Suspense, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CancelCrossIcon, Sheet } from "@activecollab/components";
import PropTypes from "prop-types";
import ErrorBoundary from "~/components/ErrorBoundry";
import { useGoogleDriveListener } from "~/components/Google/useGoogleDriveListener";
import SectionLoader from "~/components/SectionLoader/SectionLoader";
import { useObjectSheetContext } from "~/components/Sheet/ObjectSheetContext";
import { ProjectTemplateContextProvider } from "~/pages/ProjectTemplates/context";
import { TemplateDiscussionAndNote } from "~/pages/ProjectTemplates/DiscussionsAndNotes/TemplateDiscussionAndNote";
import { TemplateDiscussionAndNoteEditForm } from "~/pages/ProjectTemplates/DiscussionsAndNotes/TemplateDiscussionAndNoteEditForm";
import { resolvePageDetails } from "~/pages/ProjectTemplates/utils";
import { fetchProjectTemplateElements } from "~/store/actions/actions.projectTemplateElements";
import { fetchTemplate } from "~/store/actions/actions.projectTemplates";
import { getProjectTemplateElementById } from "~/store/selectors/selector.projectTemplateElements";
import { getProjectTemplateById } from "~/store/selectors/selector.projectTemplates";
import NotFoundSlate from "../EmptySlate/NotFoundSlate";
import { Resolver } from "../Resolver";

export const TemplateDiscussionAndNoteSheet = React.forwardRef(
  ({ elementId, templateId, onClose, open, type }, ref) => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showForm, setShowForm] = useState(location?.state?.edit || false);
    const element = useSelector((state) => getProjectTemplateElementById(state, elementId));
    const template = useSelector((state) => getProjectTemplateById(state, templateId));

    useEffect(() => {
      setShowForm(location?.state?.edit || false);
    }, [location?.state?.edit]);

    const { closeSheet } = useObjectSheetContext();
    const googlePicker = useGoogleDriveListener();

    const resolveDependencies = useCallback(async () => {
      await dispatch(fetchTemplate(templateId)).then(({ error }) => {
        if (error) {
          throw error;
        }
      });
      await dispatch(fetchProjectTemplateElements(templateId));
    }, [dispatch, templateId]);

    const handleCloseEditTaskForm = useCallback(() => {
      navigate(location.pathname + location.search, {
        replace: true,
        state: { ...(location.state || {}), edit: false },
      });
      setShowForm(false);
    }, [location.pathname, location.search, location.state, navigate]);

    const handleClose = useCallback(() => {
      if (typeof onClose === "function") onClose();
      if (showForm) handleCloseEditTaskForm();
    }, [handleCloseEditTaskForm, onClose, showForm]);

    const controls = useMemo(
      () => [
        {
          tooltip: t("Close"),
          onClick: closeSheet,
          icon: <CancelCrossIcon />,
          className: "task_modal_close_icon",
        },
      ],
      [closeSheet, t]
    );

    const pageDetails = useMemo(() => resolvePageDetails(type), [type]);

    return (
      <Sheet
        ref={ref}
        open={open}
        position="right"
        animation="right"
        mode="stretch"
        onClose={handleClose}
        controls={controls}
        className="task-modal-container"
        disableFocusLock={googlePicker}
      >
        <Suspense fallback={<SectionLoader />}>
          <ErrorBoundary errorLocation="TemplateDiscussionAndNoteSheet">
            <Resolver
              callback={resolveDependencies}
              loadingComponent={<SectionLoader />}
              errorComponent={
                <NotFoundSlate
                  header={pageDetails.header}
                  pageName={pageDetails.name}
                  pageLink={`/projects/templates/${templateId}`}
                />
              }
              update
            >
              {!element ? (
                <NotFoundSlate
                  header={pageDetails.header}
                  pageName={pageDetails.name}
                  pageLink={`/projects/templates/${templateId}`}
                />
              ) : (
                <ProjectTemplateContextProvider value={{ templateId: +templateId, template }}>
                  {!showForm ? (
                    <TemplateDiscussionAndNote element={element} />
                  ) : (
                    <TemplateDiscussionAndNoteEditForm element={element} />
                  )}
                </ProjectTemplateContextProvider>
              )}
            </Resolver>
          </ErrorBoundary>
        </Suspense>
      </Sheet>
    );
  }
);

TemplateDiscussionAndNoteSheet.propTypes = {
  elementId: PropTypes.number,
  templateId: PropTypes.number,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  type: PropTypes.string,
};

TemplateDiscussionAndNoteSheet.defaultProps = {
  taskId: 0,
  templateId: 0,
  open: false,
};

TemplateDiscussionAndNoteSheet.displayName = "TemplateDiscussionAndNoteSheet";
